import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { LuPlus, LuTrash2, LuSparkles, LuPaperclip, LuX, LuFileText, LuImage, LuFileSpreadsheet, LuFile, LuSquare, LuWrench } from "react-icons/lu";
import { AiOutlineSend } from "react-icons/ai";
import MarkdownRenderer from "../markdown/MarkdownRenderer";
import AgentSelector from "./AgentSelector";
import {
    listAgents,
    listThreads,
    getThread,
    getThreadUpdates,
    deleteThread,
    streamChat,
    resumeChat,
    uploadAttachment,
    submitRating,
} from "../../utils/agentsApi";
import {
    rowsFromPersistedUpdates,
    rowsFromStreamUpdate,
    finalizeRows,
} from "./agentMessageUtils";
import InterruptCard from "./InterruptCard";
import OutcomeRating from "./OutcomeRating";
import ThinkingPanel, { splitThinking } from "./ThinkingPanel";

const PAGE_SIZE = 30;
const THREADS_PAGE_SIZE = 25;

const SUGGESTIONS = [
    "Summarize my recent activity",
    "What can you help me with?",
    "Walk me through an example workflow",
];

const ACCEPT_TYPES = [
    "image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif",
    "application/pdf",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
    "application/json", "application/xml",
    "text/*",
    ".csv", ".tsv", ".md", ".txt", ".json", ".xml", ".log",
].join(",");

const formatBytes = (n) => {
    if (n == null) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

// Tool-call args and tool-result payloads are often huge JSON blobs. Without
// truncation, a single getClaimDetails response can fill the entire chat
// pane. We collapse anything over the threshold by default and let the user
// expand individual bubbles. Threshold is intentionally generous so short
// results (a status code, a tiny object) render fully.
const TOOL_CONTENT_COLLAPSE_THRESHOLD_CHARS = 600;
const TOOL_CONTENT_COLLAPSE_THRESHOLD_LINES = 8;

const shouldCollapseToolContent = (text) => {
    if (!text) return false;
    if (text.length > TOOL_CONTENT_COLLAPSE_THRESHOLD_CHARS) return true;
    const newlines = (text.match(/\n/g) || []).length;
    return newlines > TOOL_CONTENT_COLLAPSE_THRESHOLD_LINES;
};

const truncateToolContent = (text) => {
    if (!text) return "";
    const lines = text.split("\n");
    const truncatedByLines = lines.length > TOOL_CONTENT_COLLAPSE_THRESHOLD_LINES
        ? lines.slice(0, TOOL_CONTENT_COLLAPSE_THRESHOLD_LINES).join("\n")
        : text;
    if (truncatedByLines.length > TOOL_CONTENT_COLLAPSE_THRESHOLD_CHARS) {
        return `${truncatedByLines.slice(0, TOOL_CONTENT_COLLAPSE_THRESHOLD_CHARS).trimEnd()}…`;
    }
    return `${truncatedByLines.trimEnd()}…`;
};

const ToolBubble = ({ isCall, toolName, content }) => {
    const collapsible = shouldCollapseToolContent(content);
    const [expanded, setExpanded] = useState(false);
    const displayContent = collapsible && !expanded
        ? truncateToolContent(content)
        : content;
    return (
        <div className="agents-msg is-tool">
            <div className="agents-msg__avatar">T</div>
            <div className="agents-msg__bubble">
                <div className="agents-msg__tool-header">
                    {isCall ? "Calling" : "Tool"} · {toolName || "result"}
                </div>
                <div
                    className={
                        "agents-msg__tool-body"
                        + (collapsible && !expanded ? " is-collapsed" : "")
                    }
                >
                    {displayContent}
                </div>
                {collapsible && (
                    <button
                        type="button"
                        className="agents-msg__tool-toggle"
                        onClick={() => setExpanded((v) => !v)}
                    >
                        {expanded ? "Show less" : "Show more"}
                    </button>
                )}
            </div>
        </div>
    );
};

const fileIconFor = (mime = "", name = "") => {
    if (/^image\//.test(mime)) return LuImage;
    if (/spreadsheet|excel/.test(mime) || /\.(xlsx|xls|csv)$/i.test(name)) return LuFileSpreadsheet;
    if (mime === "application/pdf" || /\.pdf$/i.test(name)) return LuFileText;
    if (/^text\//.test(mime) || /\.(txt|md|json|xml|log|yaml|yml)$/i.test(name)) return LuFileText;
    return LuFile;
};

const initials = (label = "") => label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join("") || "AG";

const formatRelative = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return "Yesterday";
    if ((now - d) / 86400000 < 7) {
        return d.toLocaleDateString([], { weekday: "short" });
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
};

const groupKey = (iso) => {
    if (!iso) return "Older";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "Older";
    const now = new Date();
    const dayMs = 86400000;
    const startOfToday = new Date(now); startOfToday.setHours(0, 0, 0, 0);
    if (d >= startOfToday) return "Today";
    const startOfYesterday = new Date(startOfToday.getTime() - dayMs);
    if (d >= startOfYesterday) return "Yesterday";
    if (d >= new Date(startOfToday.getTime() - 7 * dayMs)) return "Previous 7 days";
    if (d >= new Date(startOfToday.getTime() - 30 * dayMs)) return "Previous 30 days";
    return "Older";
};

// Key under which a brand-new chat's run is stored before its real threadId
// arrives from the backend "thread" event.
const NEW_CHAT_KEY = "__agents_new_chat__";

const AgentsChatbot = () => {
    const queryClient = useQueryClient();
    const params = useParams();
    const navigate = useNavigate();

    const [selectedAgentId, setSelectedAgentId] = useState(
        () => localStorage.getItem("agents_selected") || "claimsAgent"
    );
    // Whether tool-call / tool-result bubbles are shown. Persisted so the
    // operator's preference survives reloads. Default to shown.
    const [showToolMessages, setShowToolMessages] = useState(
        () => localStorage.getItem("agents_show_tools") !== "0"
    );
    const [messages, setMessages] = useState([]);
    const [oldestSeq, setOldestSeq] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [textInput, setTextInput] = useState("");
    // Pending attachments before send. Each item has either {state: "uploading"}
    // (with progress) or {state: "ready", attachment} after the PUT succeeds.
    const [pendingFiles, setPendingFiles] = useState([]);

    const threadId = params.threadId || null;
    const messagesEndRef = useRef(null);
    const messagesScrollRef = useRef(null);
    const fileInputRef = useRef(null);
    // Sentinel at the top of the message list. When it scrolls into view we
    // auto-load the previous page (infinite scroll upward).
    const loadOlderRef = useRef(null);
    // Always points at the latest handleLoadMore so the IntersectionObserver
    // (set up once per thread/hasMore change) calls the current closure
    // without needing to re-create the observer on every render.
    const handleLoadMoreRef = useRef(null);
    // Tracks the thread the operator is currently viewing so a background
    // stream can tell whether its events should paint into what's on screen.
    const viewedThreadIdRef = useRef(threadId);
    // Mirror of `messages` so a newly-started run can seed its row buffer
    // synchronously (React state updates are async).
    const messagesRef = useRef([]);
    // In-flight / background streaming runs, keyed by threadId (or
    // NEW_CHAT_KEY until a brand-new chat's id arrives). Each run keeps
    // accumulating rows even after the operator switches threads, then
    // re-paints live when they return. See startRun().
    //   RunEntry = { threadId, isNewChat, rows, loading, error, controller }
    const liveRunsRef = useRef(new Map());
    // Local overlay of smiley scores so a thread-query refetch after stream
    // end cannot wipe a rating the operator just clicked (POST is in flight).
    const ratingsOverlayRef = useRef(new Map());

    const applyRatingOverlay = (rows) => {
        const overlay = ratingsOverlayRef.current;
        if (!overlay.size) return rows;
        return rows.map((r) => {
            if (r.kind !== "rating" || !r.targetId || !overlay.has(r.targetId)) return r;
            return { ...r, score: overlay.get(r.targetId) };
        });
    };

    // Identity of the last (bottom-most) message we observed. Used to detect
    // whether a render is a "new message appended" event (auto-scroll OK) or
    // a "load more" prepend (do NOT scroll — the user is reading history).
    const lastBottomIdRef = useRef(null);
    // Whether the operator was pinned to the bottom of the scroll container
    // BEFORE the most recent message arrived. The onScroll handler keeps
    // this in sync. Measuring "at the bottom" via the scroll handler (rather
    // than re-measuring inside the effect after the new content has already
    // landed) is what makes follow-the-tail reliable: a 600px-tall tool
    // result that just rendered no longer fools us into thinking the user
    // scrolled away.
    const wasAtBottomRef = useRef(true);

    useEffect(() => {
        localStorage.setItem("agents_selected", selectedAgentId);
    }, [selectedAgentId]);

    useEffect(() => {
        localStorage.setItem("agents_show_tools", showToolMessages ? "1" : "0");
    }, [showToolMessages]);

    const handleMessagesScroll = (e) => {
        const el = e.currentTarget;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        wasAtBottomRef.current = distanceFromBottom <= 80;
    };

    useEffect(() => {
        // Auto-scroll rules:
        //   • New bottom message OR streaming text grew  → follow the tail
        //     IF the operator was at the bottom right before the update.
        //   • "Load more" prepend (bottom unchanged)     → never scroll.
        //   • Operator scrolled up reading history      → never scroll.
        //   • Loading-more state toggling alone         → never scroll.
        const last = messages.length > 0 ? messages[messages.length - 1] : null;
        const lastText = last
            ? (last.text ?? last.content ?? last.message ?? "")
            : "";
        const lastFingerprint = last
            ? [
                last.seq ?? last.id ?? "",
                last.role ?? last.kind ?? last.type ?? "",
                String(lastText).length,
                last.streaming ? "s" : "",
            ].join("|")
            : null;
        const prevFingerprint = lastBottomIdRef.current;
        lastBottomIdRef.current = lastFingerprint;

        if (loadingMore) return;
        if (lastFingerprint == null) return;
        if (lastFingerprint === prevFingerprint) return;

        // First render of a freshly loaded thread always lands at the bottom
        // (prevFingerprint is null). Otherwise, only follow if the user was
        // at the bottom before this message arrived.
        if (prevFingerprint != null && !wasAtBottomRef.current) return;

        // Instant scroll (not "smooth") — during streaming we update many
        // times per second and a smooth animation racing with new deltas
        // looks janky. Following the tail should feel anchored, not
        // animated.
        const scrollEl = messagesScrollRef.current;
        if (scrollEl) {
            scrollEl.scrollTop = scrollEl.scrollHeight;
        } else {
            messagesEndRef.current?.scrollIntoView({ block: "end" });
        }
        wasAtBottomRef.current = true;
    }, [messages, loadingMore]);

    // Free any object URLs we created for image thumbnails when the
    // component unmounts.
    useEffect(() => () => {
        for (const p of pendingFiles) {
            if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const agentsQuery = useQuery({
        queryKey: ["agents", "list"],
        queryFn: listAgents,
        select: (d) => d.items,
        staleTime: 5 * 60 * 1000,
    });

    const selectedAgent = useMemo(
        () => agentsQuery.data?.find((a) => a.id === selectedAgentId),
        [agentsQuery.data, selectedAgentId]
    );

    // Role-based access can hide the persisted selection (e.g. a finance-only
    // user whose localStorage still points at claimsAgent). Once the list
    // loads, snap to the first agent the user is actually allowed to use.
    useEffect(() => {
        const list = agentsQuery.data;
        if (!list?.length) return;
        if (!list.some((a) => a.id === selectedAgentId)) {
            setSelectedAgentId(list[0].id);
        }
    }, [agentsQuery.data, selectedAgentId]);

    const threadsQueryKey = useMemo(
        () => ["agents", "threads", selectedAgentId],
        [selectedAgentId]
    );

    const threadsQuery = useInfiniteQuery({
        queryKey: threadsQueryKey,
        initialPageParam: undefined,
        queryFn: ({ pageParam }) =>
            listThreads({
                agentId: selectedAgentId,
                limit: THREADS_PAGE_SIZE,
                cursor: pageParam,
            }),
        getNextPageParam: (last) => last.nextCursor || undefined,
        staleTime: 0,
    });

    const threads = useMemo(
        () => (threadsQuery.data?.pages || [])
            .flatMap((p) => p?.items ?? [])
            .filter((t) => t && (t.threadId || t.pk)),
        [threadsQuery.data]
    );

    const groupedThreads = useMemo(() => {
        const groups = new Map();
        for (const t of threads) {
            const k = groupKey(t.updatedAt ?? t.createdAt);
            if (!groups.has(k)) groups.set(k, []);
            groups.get(k).push(t);
        }
        return Array.from(groups.entries());
    }, [threads]);

    const threadDetailQuery = useQuery({
        queryKey: ["agents", "thread", threadId],
        queryFn: () => getThread({ threadId, limit: PAGE_SIZE }),
        enabled: !!threadId,
        staleTime: 0,
    });

    useEffect(() => {
        if (!threadDetailQuery.data) return;
        setHasMore(!!threadDetailQuery.data.hasMore);
        setOldestSeq(threadDetailQuery.data.oldestSeq ?? null);
        if (threadDetailQuery.data.thread?.agentId) {
            setSelectedAgentId(threadDetailQuery.data.thread.agentId);
        }
        // If a run is actively streaming into this thread, its in-memory rows
        // are ahead of the persisted snapshot — keep them. They'll be replaced
        // by the canonical server copy once the run finishes and invalidates
        // this query.
        const entry = liveRunsRef.current.get(threadId);
        if (entry) {
            setLoading(entry.loading);
            return;
        }
        setMessages(applyRatingOverlay(rowsFromPersistedUpdates(threadDetailQuery.data.updates || [])));
    }, [threadDetailQuery.data]);

    useEffect(() => {
        if (!threadId) {
            setMessages([]);
            setHasMore(false);
            setOldestSeq(null);
        }
    }, [threadId]);

    const handleLoadMore = async () => {
        if (!threadId || oldestSeq == null || loadingMore) return;
        setLoadingMore(true);
        const scrollEl = messagesScrollRef.current;
        const prevScrollHeight = scrollEl?.scrollHeight || 0;
        try {
            const res = await getThreadUpdates({
                threadId,
                limit: PAGE_SIZE,
                before: oldestSeq,
            });
            const olderRows = rowsFromPersistedUpdates(res.updates || []);
            setMessages((prev) => applyRatingOverlay(finalizeRows([...olderRows, ...prev])));
            setHasMore(!!res.hasMore);
            setOldestSeq(res.oldestSeq ?? null);
            // Preserve scroll position after prepending older messages.
            requestAnimationFrame(() => {
                if (scrollEl) {
                    scrollEl.scrollTop = scrollEl.scrollHeight - prevScrollHeight;
                }
            });
        } catch (err) {
            setErrorMessage(err.message);
        } finally {
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        handleLoadMoreRef.current = handleLoadMore;
    });

    // Auto-trigger "load older messages" when the top sentinel scrolls into
    // view — infinite scroll going upward through history. The button is kept
    // as a visible/accessible fallback, but normally the operator never has to
    // click it. handleLoadMore already guards against concurrent/empty loads.
    useEffect(() => {
        if (!hasMore) return undefined;
        const sentinel = loadOlderRef.current;
        const root = messagesScrollRef.current;
        if (!sentinel || !root) return undefined;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    handleLoadMoreRef.current?.();
                }
            },
            // Pre-fetch a little before the sentinel is fully visible so the
            // older page is usually ready by the time the user reaches the top.
            { root, rootMargin: "150px 0px 0px 0px" }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, threadId]);

    const deleteMutation = useMutation({
        mutationFn: ({ threadId: tid }) => deleteThread({ threadId: tid }),
        onSuccess: (_data, vars) => {
            queryClient.invalidateQueries({ queryKey: ["agents", "threads"] });
            if (vars.threadId === threadId) {
                navigate("/agents");
                setMessages([]);
            }
        },
    });

    const handleFilesPicked = async (filesList) => {
        const files = Array.from(filesList || []);
        if (!files.length) return;

        const newPending = files.map((f) => ({
            id: crypto.randomUUID(),
            file: f,
            state: "uploading",
            progress: 0,
            attachment: null,
            error: null,
            // Local preview URL for images so we can render a thumbnail
            // while the upload is in flight. We revoke it when the chip is
            // removed or once the composer is cleared on submit.
            previewUrl: /^image\//.test(f.type) ? URL.createObjectURL(f) : null,
        }));
        setPendingFiles((prev) => [...prev, ...newPending]);

        for (const item of newPending) {
            try {
                const attachment = await uploadAttachment(item.file, {
                    onProgress: (p) => {
                        setPendingFiles((prev) => prev.map((x) =>
                            x.id === item.id ? { ...x, progress: p } : x
                        ));
                    },
                });
                setPendingFiles((prev) => prev.map((x) =>
                    x.id === item.id ? { ...x, state: "ready", attachment, progress: 1 } : x
                ));
            } catch (err) {
                setPendingFiles((prev) => prev.map((x) =>
                    x.id === item.id ? { ...x, state: "error", error: err.message } : x
                ));
            }
        }
    };

    const removePendingFile = (id) => {
        setPendingFiles((prev) => {
            const target = prev.find((x) => x.id === id);
            if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
            return prev.filter((x) => x.id !== id);
        });
    };

    const isAnyUploading = pendingFiles.some((p) => p.state === "uploading");
    const readyAttachments = pendingFiles
        .filter((p) => p.state === "ready" && p.attachment)
        .map((p) => p.attachment);

    // When the graph is paused on an approval, the last interrupt row is
    // unresolved. While it is, the composer can't start a normal turn (that
    // would dangle the tool_use); a typed message becomes a "decline with
    // feedback" sent through the resume endpoint. Approving (with edits) still
    // happens on the InterruptCard, which carries the editable payload.
    const pendingInterrupt = useMemo(() => {
        for (let i = messages.length - 1; i >= 0; i -= 1) {
            if (messages[i]?.kind === "interrupt") {
                return messages[i].resolved ? null : messages[i];
            }
        }
        return null;
    }, [messages]);

    // Finds the background run (if any) whose output belongs to the thread the
    // operator is currently looking at. A new-chat run "owns" the empty
    // /agents screen (threadId null) until its real id arrives.
    const getViewedRun = () => {
        const vid = viewedThreadIdRef.current ?? null;
        for (const entry of liveRunsRef.current.values()) {
            if ((entry.threadId ?? null) === vid) return entry;
            if (entry.isNewChat && vid == null) return entry;
        }
        return null;
    };

    // Core streaming engine, shared by submit() and submitDecision().
    //
    // A run accumulates rows into its own `entry.rows` buffer regardless of
    // which thread is on screen, so generation keeps flowing in the background
    // when the operator switches away. It only paints into the live UI while
    // its thread is the one being viewed (`paint()` guards on owns()). When the
    // operator returns to a streaming thread, the thread-change effect re-binds
    // the view to the run's buffer and subsequent events paint live again.
    const startRun = async ({ streamFactory, runThreadId, isNewChat, base }) => {
        const controller = new AbortController();
        let key = runThreadId ?? NEW_CHAT_KEY;
        const entry = {
            threadId: runThreadId ?? null,
            isNewChat: !!isNewChat,
            rows: base,
            loading: true,
            error: "",
            controller,
        };
        liveRunsRef.current.set(key, entry);

        const owns = () => {
            const vid = viewedThreadIdRef.current ?? null;
            return (entry.threadId ?? null) === vid || (entry.isNewChat && vid == null);
        };
        const paint = () => {
            if (!owns()) return;
            setMessages(entry.rows);
            setLoading(entry.loading);
            setErrorMessage(entry.error || "");
        };

        // Paint the optimistic human row + loading state immediately.
        paint();

        let resolvedThreadId = runThreadId;
        try {
            const stream = streamFactory(controller.signal);
            for await (const evt of stream) {
                if (evt.type === "thread") {
                    resolvedThreadId = evt.threadId;
                    // Re-key a brand-new chat under its real id so the operator
                    // can return to it (via the sidebar) while it streams.
                    if (entry.threadId == null && resolvedThreadId) {
                        entry.threadId = resolvedThreadId;
                        liveRunsRef.current.delete(key);
                        key = resolvedThreadId;
                        liveRunsRef.current.set(key, entry);
                        queryClient.invalidateQueries({ queryKey: ["agents", "threads"] });
                    }
                    continue;
                }
                // The user prompt is already rendered optimistically; `end` is a
                // sentinel with no row payload.
                if (evt.type === "user_prompt" || evt.type === "end") continue;
                // ai_delta / tool_call / tool_result / interrupt / error all flow
                // through the same shared row converter, then we run a
                // resolveInterrupts pass so any in-flight interrupt_resume
                // event flips its matching card to a resolved state.
                const rows = rowsFromStreamUpdate(evt);
                if (rows.length) {
                    entry.rows = applyRatingOverlay(finalizeRows([...entry.rows, ...rows]));
                    paint();
                }
            }
            entry.loading = false;
            paint();
            // Move the operator into the new thread only if they're still on the
            // new-chat screen (they may have navigated elsewhere mid-stream).
            if (isNewChat && resolvedThreadId && (viewedThreadIdRef.current ?? null) == null) {
                navigate(`/agents/${resolvedThreadId}`);
            }
        } catch (err) {
            entry.loading = false;
            if (controller.signal.aborted || err.name === "AbortError") {
                entry.error = "Stopped.";
                if (isNewChat && resolvedThreadId && (viewedThreadIdRef.current ?? null) == null) {
                    navigate(`/agents/${resolvedThreadId}`);
                }
            } else {
                entry.error = err.message || "Something went wrong.";
            }
            paint();
        } finally {
            entry.controller = null;
            liveRunsRef.current.delete(key);
            // Refresh the sidebar and, if it's open, the persisted thread detail
            // so the canonical server copy replaces our in-memory live rows.
            queryClient.invalidateQueries({ queryKey: ["agents", "threads"] });
            if (entry.threadId) {
                queryClient.invalidateQueries({ queryKey: ["agents", "thread", entry.threadId] });
            }
        }
    };

    const submit = async (text) => {
        const trimmed = (text || "").trim();
        if ((!trimmed && !readyAttachments.length) || loading || isAnyUploading) return;

        setErrorMessage("");
        const sentAttachments = readyAttachments;
        const humanRow = {
            kind: "human",
            content: trimmed,
            attachments: sentAttachments.length ? sentAttachments : undefined,
        };
        setPendingFiles((prev) => {
            for (const p of prev) if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
            return [];
        });

        const runThreadId = threadId;
        const base = [...messagesRef.current, humanRow];

        await startRun({
            runThreadId,
            isNewChat: !runThreadId,
            base,
            streamFactory: (signal) => streamChat({
                agentId: selectedAgentId,
                message: trimmed,
                threadId: runThreadId || undefined,
                attachments: sentAttachments.length ? sentAttachments : undefined,
                signal,
            }),
        });
    };

    // Stops only the run feeding the thread currently on screen. Other
    // background runs keep going.
    const stopStreaming = () => {
        const entry = getViewedRun();
        entry?.controller?.abort();
    };

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    useEffect(() => {
        viewedThreadIdRef.current = threadId;
    }, [threadId]);

    // When the operator switches threads, re-bind the view to that thread's
    // background run (if one is streaming) so its live rows + loading state
    // show immediately; otherwise reset the streaming UI state (the detail
    // query repopulates the messages for an idle thread).
    useEffect(() => {
        const entry = liveRunsRef.current.get(threadId);
        if (entry) {
            setMessages(applyRatingOverlay(entry.rows));
            setLoading(entry.loading);
            setErrorMessage(entry.error || "");
        } else {
            setLoading(false);
            setErrorMessage("");
        }
    }, [threadId]);

    // On unmount, abort every in-flight run.
    useEffect(() => () => {
        for (const entry of liveRunsRef.current.values()) {
            entry.controller?.abort();
        }
    }, []);

    // Sends the operator's approval/rejection back to the paused graph and
    // streams the rest of the turn (tool execution + final AI response).
    const submitDecision = async ({ interruptId, decision }) => {
        if (!threadId || loading) return;
        setErrorMessage("");
        const runThreadId = threadId;
        const base = messagesRef.current;
        await startRun({
            runThreadId,
            isNewChat: false,
            base,
            streamFactory: (signal) => resumeChat({
                agentId: selectedAgentId,
                threadId: runThreadId,
                interruptId,
                decision,
                signal,
            }),
        });
    };

    // Optimistic smiley — persist + skill learn run in the background so
    // the chat is never blocked. Overlay survives a thread-query refetch.
    const handleRate = (row, score) => {
        if (!threadId || !row?.targetId) return;
        const already = Number(row.score);
        if (Number.isInteger(already) && already >= 1 && already <= 5) return;
        if (ratingsOverlayRef.current.has(row.targetId)) return;
        ratingsOverlayRef.current.set(row.targetId, score);
        const apply = (rows) => rows.map((r) => (
            r.kind === "rating" && r.targetId === row.targetId
                ? { ...r, score }
                : r
        ));
        setMessages((prev) => apply(prev));
        const entry = liveRunsRef.current.get(threadId);
        if (entry) entry.rows = apply(entry.rows);
        submitRating({
            threadId,
            targetId: row.targetId,
            score,
            toolName: row.toolName,
            agentId: selectedAgentId,
        }).catch((err) => {
            console.error("[agents] rating failed", err);
        });
    };

    const handleFormSubmit = (e) => {
        e.preventDefault();
        const t = textInput;
        const trimmed = t.trim();
        // While an approval is pending, a typed message declines it with the
        // text as feedback (use the card above to approve). Routing through
        // the resume endpoint keeps the tool_use/tool_result pairing valid and
        // flips the approval card to "Declined" with the reason.
        if (pendingInterrupt) {
            if (!trimmed || loading) return;
            setTextInput("");
            submitDecision({
                interruptId: pendingInterrupt.interruptId,
                decision: { approved: false, reason: trimmed },
            });
            return;
        }
        setTextInput("");
        submit(t);
    };

    const onSelectAgent = (id) => {
        setSelectedAgentId(id);
        if (threadId) {
            navigate("/agents");
            setMessages([]);
        }
    };

    const renderAttachmentChip = (a, i) => {
        const Icon = fileIconFor(a.mime, a.name);
        const isImage = /^image\//.test(a.mime || "");
        if (isImage && a.downloadUrl) {
            return (
                <a
                    key={`${a.key}-${i}`}
                    className="agents-attachment is-image"
                    href={a.downloadUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={a.name}
                >
                    <img src={a.downloadUrl} alt={a.name} />
                </a>
            );
        }
        const inner = (
            <>
                <div className="agents-attachment__icon"><Icon size={"1rem"} /></div>
                <div className="agents-attachment__body">
                    <div className="agents-attachment__name">{a.name}</div>
                    <div className="agents-attachment__meta">
                        {a.mime || "file"}{a.size ? ` · ${formatBytes(a.size)}` : ""}
                    </div>
                </div>
            </>
        );
        if (a.downloadUrl) {
            return (
                <a key={`${a.key}-${i}`} className="agents-attachment" href={a.downloadUrl} target="_blank" rel="noreferrer">
                    {inner}
                </a>
            );
        }
        return <div key={`${a.key}-${i}`} className="agents-attachment">{inner}</div>;
    };

    const renderMessageBubble = (m, idx) => {
        const key = `${m.seq ?? "live"}-${idx}`;
        const isLastMessage = idx === messages.length - 1;
        if (m.kind === "human") {
            return (
                <div className="agents-msg is-human" key={key}>
                    <div className="agents-msg__bubble">
                        {m.attachments?.length ? (
                            <div className="agents-msg__attachments">
                                {m.attachments.map(renderAttachmentChip)}
                            </div>
                        ) : null}
                        {m.content && (
                            <div className="agents-msg__text">{m.content}</div>
                        )}
                    </div>
                </div>
            );
        }
        if (m.kind === "interrupt") {
            return (
                <InterruptCard
                    key={key}
                    interrupt={m}
                    disabled={loading}
                    onDecide={submitDecision}
                />
            );
        }
        if (m.kind === "rating") {
            return (
                <div className="agents-msg is-rating" key={key}>
                    <OutcomeRating
                        prompt={m.prompt}
                        score={m.score}
                        disabled={!threadId}
                        onRate={(score) => handleRate(m, score)}
                    />
                </div>
            );
        }
        if (m.kind === "error") {
            return (
                <div className="agents-msg is-error" key={key}>
                    <div className="agents-msg__error-bubble" role="alert">
                        {m.content || "Something went wrong."}
                    </div>
                </div>
            );
        }
        if (m.kind === "tool" || m.kind === "tool_call") {
            if (!showToolMessages) return null;
            return (
                <ToolBubble
                    key={key}
                    isCall={m.kind === "tool_call"}
                    toolName={m.toolName}
                    content={m.content}
                />
            );
        }
        // Two reasoning sources:
        //   1. `m.reasoning` — Anthropic-style extended thinking emitted on
        //      the `ai_reasoning_delta` channel. Live or replayed.
        //   2. `<thinking>…</thinking>` tags inside `m.content` — Nova-style
        //      inline reasoning. Kept for backward compat.
        const inline = splitThinking(m.content || "");
        const reasoningFromRow = (m.reasoning || "").trim();
        const thinking = [reasoningFromRow, inline.thinking]
            .filter(Boolean)
            .join("\n\n")
            .trim();
        const cleanedVisible = inline.visible.replace(/(\[.*?\])/g, "$1\n").trim();
        // While streaming, treat the panel as "in flight" if either inline
        // tags are still open OR we have reasoning text but no visible
        // reply yet (live extended thinking arriving before the answer).
        const reasoningStreaming = inline.streaming
            || (loading && isLastMessage && !!reasoningFromRow && !cleanedVisible);
        // Don't render a fully-empty AI bubble when there's nothing to
        // show (e.g. a pure tool call setup). The tool/result bubble below
        // already represents that turn.
        if (!cleanedVisible && !thinking) return null;
        return (
            <div className="agents-msg is-ai" key={key}>
                <div className="agents-msg__avatar">AI</div>
                <div className="agents-msg__bubble ai-message">
                    {thinking && (
                        <ThinkingPanel
                            thinking={thinking}
                            streaming={reasoningStreaming}
                        />
                    )}
                    {cleanedVisible
                        ? <MarkdownRenderer>{cleanedVisible}</MarkdownRenderer>
                        : (loading && isLastMessage)
                            ? (
                                <div className="agents-chat__typing" aria-label="Assistant is typing">
                                    <span /><span /><span />
                                </div>
                            )
                            // Stream is done but the model produced no
                            // visible text (e.g. content-filter blocked it,
                            // or it only emitted a tool-use turn). The
                            // ThinkingPanel above still shows the reasoning;
                            // we just need to make it clear the turn ended
                            // instead of leaving spinning dots forever.
                            : !thinking
                                ? null
                                : (
                                    <div className="agents-msg__empty-reply">
                                        No reply generated.
                                    </div>
                                )
                    }
                </div>
            </div>
        );
    };

    const startNewChat = () => {
        ratingsOverlayRef.current = new Map();
        setMessages([]);
        setErrorMessage("");
        navigate("/agents");
    };

    return (
        <div className="agents-workspace">
            <AgentSelector
                agents={agentsQuery.data}
                selectedAgentId={selectedAgentId}
                onSelect={onSelectAgent}
                isLoading={agentsQuery.isLoading}
                error={agentsQuery.error?.message}
            />

            <section className="threads-pane">
                <div className="threads-pane__header">
                    <div className="threads-pane__title">
                        {selectedAgent?.label || selectedAgentId}
                    </div>
                    {selectedAgent?.description && (
                        <div className="threads-pane__subtitle">
                            {selectedAgent.description}
                        </div>
                    )}
                    <button className="threads-pane__new" onClick={startNewChat}>
                        <LuPlus size={"1rem"} /> New chat
                    </button>
                </div>

                <div className="threads-pane__list scrollable-form">
                    {threadsQuery.isError && (
                        <p className="danger" style={{ padding: ".5rem .8rem" }}>
                            {threadsQuery.error?.message}
                        </p>
                    )}
                    {threadsQuery.isLoading && (
                        <div className="loader" style={{ margin: "1rem auto" }} />
                    )}

                    {!threadsQuery.isLoading && !threads.length && (
                        <div className="threads-pane__empty">
                            No conversations yet. Start a new chat to begin.
                        </div>
                    )}

                    {groupedThreads.map(([label, items]) => (
                        <div key={label}>
                            <div className="threads-section-label">{label}</div>
                            {items.filter(Boolean).map((t) => (
                                <div
                                    key={t.threadId || t.pk}
                                    className={`thread-item ${threadId === t.threadId ? "is-active" : ""}`}
                                    onClick={() => navigate(`/agents/${t.threadId}`)}
                                >
                                    <div className="thread-item__body">
                                        <div className="thread-item__title">
                                            {t.title || t.firstMessage || "Untitled chat"}
                                        </div>
                                        {t.latestMessage && (
                                            <div className="thread-item__preview">
                                                {t.latestMessage}
                                            </div>
                                        )}
                                        <div className="thread-item__meta">
                                            {formatRelative(t.updatedAt ?? t.createdAt)}
                                        </div>
                                    </div>
                                    <div
                                        className="thread-item__delete"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm("Delete this conversation?")) {
                                                deleteMutation.mutate({ threadId: t.threadId });
                                            }
                                        }}
                                        title="Delete chat"
                                    >
                                        <LuTrash2 size={"1rem"} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ))}

                    {threadsQuery.hasNextPage && (
                        <button
                            className="threads-pane__more"
                            onClick={() => threadsQuery.fetchNextPage()}
                            disabled={threadsQuery.isFetchingNextPage}
                        >
                            {threadsQuery.isFetchingNextPage ? "Loading…" : "Load more"}
                        </button>
                    )}
                </div>
            </section>

            <main className="agents-chat">
                <header className="agents-chat__header">
                    <div className="agents-chat__header-left">
                        <div className="agents-chat__avatar">
                            {initials(selectedAgent?.label || selectedAgentId)}
                        </div>
                        <div className="agents-chat__heading">
                            <div className="agents-chat__title">
                                {selectedAgent?.label || selectedAgentId}
                            </div>
                            <div className="agents-chat__subtitle">
                                {selectedAgent?.description || "AI agent"}
                            </div>
                        </div>
                    </div>
                    <div className="agents-chat__header-right">
                        <button
                            type="button"
                            className={`agents-chat__tool-toggle ${showToolMessages ? "is-on" : ""}`}
                            onClick={() => setShowToolMessages((v) => !v)}
                            aria-pressed={showToolMessages}
                            title={showToolMessages ? "Hide tool calls & results" : "Show tool calls & results"}
                        >
                            <LuWrench size={"0.95rem"} />
                            {showToolMessages ? "Hide tools" : "Show tools"}
                        </button>
                        {selectedAgent?.runtime && (
                            <span className="agents-chat__pill">{selectedAgent.runtime}</span>
                        )}
                    </div>
                </header>

                {!messages.length ? (
                    <div className="agents-chat__empty">
                        <div className="agents-chat__empty-card">
                            <div className="agents-chat__empty-emoji">
                                <LuSparkles size={"1.6rem"} />
                            </div>
                            <div className="agents-chat__empty-title">
                                How can I help you today?
                            </div>
                            <div className="agents-chat__empty-sub">
                                Chatting with{" "}
                                <strong>{selectedAgent?.label || selectedAgentId}</strong>
                                {selectedAgent?.description ? ` — ${selectedAgent.description}` : ""}.
                            </div>
                            <div className="agents-chat__suggestions">
                                {SUGGESTIONS.map((s) => (
                                    <button
                                        key={s}
                                        className="agents-chat__suggestion"
                                        onClick={() => submit(s)}
                                        disabled={loading}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        className="agents-chat__messages"
                        ref={messagesScrollRef}
                        onScroll={handleMessagesScroll}
                    >
                        <div className="agents-chat__messages-inner">
                            {hasMore && (
                                <button
                                    ref={loadOlderRef}
                                    className="agents-chat__load-older"
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                >
                                    {loadingMore ? "Loading…" : "Load older messages"}
                                </button>
                            )}
                            {messages.map(renderMessageBubble)}
                            {loading && (() => {
                                // Only show a standalone typing bubble while
                                // we're _waiting_ for the AI to start. Once
                                // an AI row exists in `messages`, the
                                // streaming text (or the in-bubble typing
                                // placeholder) lives inside that bubble, so
                                // a second floating loader would just look
                                // like a duplicate empty message.
                                const last = messages[messages.length - 1];
                                const aiBubbleAlreadyShown =
                                    last && (last.kind === "ai" || last.kind === "interrupt");
                                if (aiBubbleAlreadyShown) return null;
                                return (
                                    <div className="agents-msg is-ai">
                                        <div className="agents-msg__avatar">AI</div>
                                        <div className="agents-msg__bubble">
                                            <div
                                                className="agents-chat__typing"
                                                aria-label="Assistant is typing"
                                            >
                                                <span /><span /><span />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                            <div ref={messagesEndRef} />
                        </div>
                    </div>
                )}

                <div className="agents-chat__composer">
                    <div className="agents-chat__composer-inner">
                        {errorMessage && (
                            <div className="agents-chat__error">{errorMessage}</div>
                        )}

                        {pendingInterrupt && !loading && (
                            <div className="agents-chat__interrupt-hint">
                                Waiting for your approval above. Approve or edit
                                using the card; sending a message declines it
                                with your note as the reason.
                            </div>
                        )}

                        {pendingFiles.length > 0 && (
                            <div className="agents-chat__pending">
                                {pendingFiles.map((p) => {
                                    const Icon = fileIconFor(p.file.type, p.file.name);
                                    const isImage = !!p.previewUrl;
                                    return (
                                        <div
                                            key={p.id}
                                            className={`agents-pending ${isImage ? "is-image" : ""} ${p.state === "error" ? "is-error" : ""}`}
                                        >
                                            <div className="agents-pending__icon">
                                                {isImage
                                                    ? <img src={p.previewUrl} alt={p.file.name} />
                                                    : <Icon size={"1rem"} />}
                                            </div>
                                            <div className="agents-pending__body">
                                                <div className="agents-pending__name">{p.file.name}</div>
                                                <div className="agents-pending__meta">
                                                    {p.state === "uploading" && `Uploading… ${Math.round((p.progress || 0) * 100)}%`}
                                                    {p.state === "ready" && formatBytes(p.file.size)}
                                                    {p.state === "error" && (p.error || "Upload failed")}
                                                </div>
                                                {p.state === "uploading" && (
                                                    <div className="agents-pending__bar">
                                                        <span style={{ width: `${(p.progress || 0) * 100}%` }} />
                                                    </div>
                                                )}
                                            </div>
                                            <button
                                                type="button"
                                                className="agents-pending__remove"
                                                onClick={() => removePendingFile(p.id)}
                                                aria-label="Remove attachment"
                                            >
                                                <LuX size={"0.9rem"} />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        )}

                        <form className="agents-chat__form" onSubmit={handleFormSubmit}>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                accept={ACCEPT_TYPES}
                                onChange={(e) => {
                                    handleFilesPicked(e.target.files);
                                    e.target.value = "";
                                }}
                                style={{ display: "none" }}
                            />
                            <button
                                type="button"
                                className="agents-chat__attach"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={loading || !!pendingInterrupt}
                                aria-label="Attach files"
                                title={pendingInterrupt
                                    ? "Resolve the pending approval first"
                                    : "Attach files"}
                            >
                                <LuPaperclip size={"1.05rem"} />
                            </button>
                            <input
                                type="text"
                                value={textInput}
                                onChange={(e) => setTextInput(e.target.value)}
                                placeholder={
                                    loading
                                        ? "Waiting for response…"
                                        : pendingInterrupt
                                            ? "Send a message to decline the approval above…"
                                            : `Message ${selectedAgent?.label || selectedAgentId}`
                                }
                                disabled={loading}
                            />
                            {loading ? (
                                <button
                                    type="button"
                                    className="agents-chat__send is-stop"
                                    onClick={stopStreaming}
                                    aria-label="Stop generating"
                                    title="Stop generating"
                                >
                                    <LuSquare size={"1rem"} fill="currentColor" />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    className="agents-chat__send"
                                    disabled={isAnyUploading || (!textInput.trim() && (!!pendingInterrupt || !readyAttachments.length))}
                                    aria-label="Send message"
                                >
                                    <AiOutlineSend size={"1.1rem"} />
                                </button>
                            )}
                        </form>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AgentsChatbot;
